'use client'

import Input from './Input'
import PhoneInput from './PhoneInput'
import EmailInput from './EmailInput'
import Textarea from './Textarea'
import Select from '../ui/Select'
import CheckboxInput from './CheckboxInput'
import clsx from 'clsx'
import type { FormFieldFragment } from '@/lib/types/datocms/graphql/generated'
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form'
import Icon from '../svg/icon'
import { sanitizeFieldName } from '@/utils/sanitizedLabel'
import MultipleTextInput from './MultipleTextInput'
import FileInput from './FileInput'
import CurrentDateInput from './CurrentDateInput'
import DatePickerInput from './DatePickerInput'
import MultipleChoice from './MultipleChoice'
import SingleChoice from './SingleChoice'

type FormFieldProps = {
	field: FormFieldFragment
	register: UseFormRegister<any>
	errors: FieldErrors
	formStyle: string | null
	footerForm?: boolean
	loading?: boolean
	control: Control<any>
	keyField: string
}

export default function FormField({
	field,
	register,
	errors,
	formStyle = 'box',
	footerForm,
	loading,
	control,
	keyField,
}: Readonly<FormFieldProps>) {
	switch (field.fieldType) {
		case 'text':
			return (
				<Input
					key={(field.id as string) ?? ''}
					id={field.id as string}
					label={field.label as string}
					required={field.required as boolean}
					register={register}
					errors={errors}
					colSpan={field.colSpan as string}
					formStyle={formStyle ?? 'box'}
					className={clsx(formStyle === 'box' && 'text-blue-dark')}
				/>
			)
		case 'phone':
			return (
				<PhoneInput
					key={(field.id as string) ?? ''}
					id={field.id as string}
					label={field.label as string}
					required={field.required as boolean}
					register={register}
					errors={errors}
					colSpan={field.colSpan as string}
					formStyle={formStyle ?? 'box'}
					className={clsx(formStyle === 'box' && 'text-blue-dark')}
				/>
			)
		case 'email':
			if (footerForm) {
				return (
					<div className="flex gap-4 items-start" key={(field.id as string) ?? ''}>
						<EmailInput
							id={field.id as string}
							required={field.required as boolean}
							register={register}
							errors={errors}
							label={field.label as string}
							formStyle={formStyle ?? 'box'}
							className="w-full"
							colSpan={field.colSpan as string}
						/>
						<button
							className={clsx(
								'group relative flex items-center justify-center h-[60px] w-[60px] rounded-lg border transition-colors group shrink-0 disabled:opacity-50 overflow-hidden',
								'outline-white'
							)}
							disabled={loading}
							type="submit"
							aria-label="Submit Form"
						>
							<span className="arrow-container -rotate-45 -ml-8">
								<Icon
									icon="Arrow"
									className={clsx(
										'absolute w-6 h-6 translate-x-0 group-hover:translate-x-[250%] transition-transform duration-0 m-ease group-hover:duration-300'
									)}
								/>
								<Icon
									icon="Arrow"
									className={clsx(
										'absolute w-6 h-6 -translate-x-[250%] group-hover:translate-x-0 transition-transform duration-0 m-ease group-hover:duration-300'
									)}
								/>
							</span>
						</button>
					</div>
				)
			}
			return (
				<EmailInput
					key={(field.id as string) ?? ''}
					id={field.id as string}
					required={field.required as boolean}
					register={register}
					errors={errors}
					label={field.label as string}
					colSpan={field.colSpan as string}
					formStyle={formStyle ?? 'box'}
					className={clsx(formStyle === 'box' && 'text-blue-dark')}
				/>
			)
		case 'text-area':
			return (
				<Textarea
					key={(field.id as string) ?? ''}
					id={field.id as string}
					label={field.label as string}
					required={field.required as boolean}
					register={register}
					errors={errors}
					colSpan={field.colSpan as string}
					formStyle={formStyle ?? 'box'}
					className={clsx(formStyle === 'box' && 'text-blue-dark')}
				/>
			)
		case 'select':
			return (
				<Controller
					name={sanitizeFieldName(field.label ?? '')}
					control={control}
					defaultValue=""
					rules={{
						required: field.required ? `${field.label} is required` : false,
					}}
					render={({ field: { onChange, value, ref } }) => (
						<Select
							key={`${field.id}-${keyField}`}
							id={field.id as string}
							label={field.label as string}
							options={field.selectOptions?.map((option) => option.value as string) ?? []}
							colSpan={field.colSpan as string}
							formStyle={formStyle ?? 'box'}
							alignment="space-between"
							required={field.required as boolean}
							errors={errors}
							className={clsx(formStyle === 'box' && 'text-blue-dark')}
							value={value}
							onChange={onChange}
							inputRef={ref}
						/>
					)}
				/>
			)
		case 'checkbox': {
			const fieldName = sanitizeFieldName(field.label ?? '')
			return (
				<div
					className={clsx(
						'form-component',
						{
							'2': 'colSpan2',
						}[field.colSpan as string]
					)}
				>
					<label key={(field.id as string) ?? ''} className="flex w-fit flex-row items-start gap-2">
						<Controller
							name={fieldName}
							control={control}
							defaultValue={false}
							rules={{
								required: field.required ? `${field.label} is required` : false,
							}}
							render={({ field: { onChange, value } }) => (
								<CheckboxInput
									id={field.id as string}
									colSpan={field.colSpan as string}
									register={register(fieldName, {
										required: {
											value: field.required as boolean,
											message: `${field.label} is required`,
										},
									})}
									checked={value}
									onChange={onChange}
								/>
							)}
						/>
						<span>{field.label}</span>
					</label>
					{errors?.[fieldName] && (
						<span className="mt-1 text-red-500">{errors[fieldName]?.message as string}</span>
					)}
				</div>
			)
		}
		case 'multipleChoice':
			return (
				<MultipleChoice
					id={field.id as string}
					label={field.label as string}
					required={field.required as boolean}
					formStyle={formStyle ?? 'box'}
					register={register}
					errors={errors}
					selectOptions={field.selectOptions?.map((option) => option.value as string) ?? []}
					control={control}
					colSpan={field.colSpan as string}
				/>
			)
		case 'multipleTextInput':
			return (
				<MultipleTextInput
					id={field.id as string}
					label={field.label as string}
					required={field.required as boolean}
					formStyle={formStyle ?? 'box'}
					register={register}
					errors={errors}
					control={control}
					colSpan={field.colSpan as string}
				/>
			)
		case 'chooseFile':
			if (!field.fileUploadOptions?.length) {
				// Default single file upload if no options
				return (
					<FileInput
						id={field.id as string}
						label={field.label as string}
						required={field.required as boolean}
						formStyle={formStyle ?? 'box'}
						register={register}
						control={control}
						errors={errors}
						accept="image/*,application/pdf"
						colSpan={field.colSpan as string}
					/>
				)
			}

			// Render multiple file inputs based on options
			return (
				<div
					className={clsx(
						'form-component',
						{
							'2': 'colSpan2',
						}[field.colSpan as string]
					)}
				>
					<label className="block mb-4">{field.label}</label>
					<div
						className={clsx(
							'grid grid-cols-1 gap-4',
							{
								2: '@xl:grid-cols-2',
								3: '@xl:grid-cols-3',
								4: '@xl:grid-cols-4',
								5: '@xl:grid-cols-3',
								6: '@xl:grid-cols-3',
								7: '@xl:grid-cols-3',
								8: '@xl:grid-cols-4',
							}[field.fileUploadOptions.length]
						)}
					>
						{field.fileUploadOptions.map((option, index) => (
							<FileInput
								key={(option.id as string) ?? ''}
								id={`${field.id}_${index}`}
								label={option.label as string}
								required={option.required}
								formStyle={formStyle ?? 'box'}
								register={register}
								control={control}
								errors={errors}
								accept="image/*,application/pdf"
								colSpan={field.colSpan as string}
								hideMainLabel
							/>
						))}
					</div>
				</div>
			)
		case 'currentDate':
			return (
				<CurrentDateInput
					id={(field.id as string) ?? ''}
					label={field.label as string}
					required={field.required as boolean}
					formStyle={formStyle ?? 'box'}
					register={register}
					errors={errors}
					colSpan={field.colSpan as string}
				/>
			)
		case 'date':
			return (
				<DatePickerInput
					id={(field.id as string) ?? ''}
					label={field.label as string}
					required={field.required as boolean}
					formStyle={formStyle ?? 'box'}
					register={register}
					errors={errors}
					colSpan={field.colSpan as string}
				/>
			)

		case 'singleChoice':
			return (
				<SingleChoice
					id={(field.id as string) ?? ''}
					label={field.label as string}
					required={field.required as boolean}
					formStyle={formStyle ?? 'box'}
					register={register}
					errors={errors}
					selectOptions={field.selectOptions?.map((option) => option.value as string) ?? []}
					colSpan={field.colSpan as string}
				/>
			)

		default:
			return null
	}
}
